import { zodResolver } from '@hookform/resolvers/zod';
import {
  CreateInclusionExclusionRequestSchema,
  type CreateInclusionExclusionRequest,
  INCLUSION_EXCLUSION_TYPES,
  UpdateInclusionExclusionRequestSchema,
  type UpdateInclusionExclusionRequest,
  type InclusionExclusionDetail,
  type InclusionExclusionListItem,
} from '@utazzo/shared';
import { UzButton, UzDrawer, UzForm, UzFormField, UzInput, UzSelect } from '@utazzo/ui';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  useCreateInclusionExclusion,
  useUpdateInclusionExclusion,
} from '../use-inclusions-exclusions';

type IEType = (typeof INCLUSION_EXCLUSION_TYPES)[number];

interface InclusionExclusionFormDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  entry?: InclusionExclusionDetail | InclusionExclusionListItem | null;
  /** Pre-selected type for create (from the active list filter). */
  defaultType: IEType;
}

const TYPE_OPTIONS = INCLUSION_EXCLUSION_TYPES.map((t) => ({
  value: t,
  label: t.charAt(0).toUpperCase() + t.slice(1),
}));

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

const buildCreateDefaults = (type: IEType): CreateInclusionExclusionRequest => ({
  type,
  title: '',
  isActive: true,
});

/** Shared field set. Reads the active form from context (UzForm = FormProvider). */
const EntryFields = ({ pending }: { pending: boolean }): React.JSX.Element => {
  return (
    <>
      <UzFormField name="type">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Type"
            required
            options={TYPE_OPTIONS}
            value={(field.value as string) || 'inclusion'}
            onValueChange={field.onChange}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="title">
        {({ field, error }) => (
          <UzInput
            {...field}
            value={(field.value as string | undefined) ?? ''}
            label="Title"
            placeholder="Return airfare"
            error={error}
            required
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="isActive">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Status"
            required
            options={STATUS_OPTIONS}
            value={field.value === false ? 'inactive' : 'active'}
            onValueChange={(v) => field.onChange(v === 'active')}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </>
  );
};

export const InclusionExclusionFormDrawer = ({
  open,
  onOpenChange,
  mode,
  entry,
  defaultType,
}: InclusionExclusionFormDrawerProps): React.JSX.Element => {
  const isCreate = mode === 'create';
  const createEntry = useCreateInclusionExclusion();
  const updateEntry = useUpdateInclusionExclusion();
  const pending = createEntry.isPending || updateEntry.isPending;

  const createForm = useForm<CreateInclusionExclusionRequest>({
    resolver: zodResolver(CreateInclusionExclusionRequestSchema),
    defaultValues: buildCreateDefaults(defaultType),
  });
  const updateForm = useForm<UpdateInclusionExclusionRequest>({
    resolver: zodResolver(UpdateInclusionExclusionRequestSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (!open) return;
    if (isCreate) {
      createForm.reset(buildCreateDefaults(defaultType));
    } else if (entry) {
      const d = entry as InclusionExclusionDetail;
      updateForm.reset({
        type: (d.type as IEType) ?? defaultType,
        title: d.title,
        isActive: d.isActive,
      });
    }
  }, [open, isCreate, entry, defaultType, createForm, updateForm]);

  const onCreate = createForm.handleSubmit(async (values) => {
    await createEntry.mutateAsync(values);
    onOpenChange(false);
  });

  const onUpdate = updateForm.handleSubmit(async (values) => {
    if (!entry) return;
    await updateEntry.mutateAsync({ id: entry.id, payload: values });
    onOpenChange(false);
  });

  return (
    <UzDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isCreate ? 'Add entry' : `Edit ${entry?.title ?? 'entry'}`}
      description="Inclusion / exclusion master entry."
      footer={
        <div className="flex justify-end gap-2">
          <UzButton variant="ghost" onClick={() => onOpenChange(false)} disabled={pending}>
            Cancel
          </UzButton>
          <UzButton onClick={isCreate ? onCreate : onUpdate} loading={pending}>
            {isCreate ? 'Submit' : 'Save changes'}
          </UzButton>
        </div>
      }
    >
      {isCreate ? (
        <UzForm form={createForm} onSubmit={onCreate}>
          <EntryFields pending={pending} />
        </UzForm>
      ) : (
        <UzForm form={updateForm} onSubmit={onUpdate}>
          <EntryFields pending={pending} />
        </UzForm>
      )}
    </UzDrawer>
  );
};
